import { useEffect, useRef, useState } from 'react';
import { getFeedback } from '../services/feedbackService';

const PAGE_SIZE = 10;

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function getPageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const items = [1];

  if (current > 3) items.push('…');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let pageNum = start; pageNum <= end; pageNum += 1) {
    items.push(pageNum);
  }

  if (current < total - 2) items.push('…');

  items.push(total);
  return items;
}

function StarRating({ rating }) {
  const value = Math.max(0, Math.min(5, Number(rating) || 0));

  return (
    <div className="feedback-rating" aria-label={`${value} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, index) => (
        <img
          key={index}
          alt=""
          className={`feedback-star ${index < value ? 'filled' : ''}`}
          src={`${process.env.PUBLIC_URL}/images/star.svg`}
        />
      ))}
      <span className="feedback-rating-value">{value}</span>
    </div>
  );
}

export default function Feedback() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [ratingFilter, setRatingFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const scrollRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;

    async function loadFeedback() {
      setLoading(true);
      setError('');

      try {
        const response = await getFeedback({
          page,
          limit: PAGE_SIZE,
          rating: ratingFilter || undefined,
          search: debouncedSearch || undefined,
        });

        if (cancelled) return;

        setItems(Array.isArray(response.data) ? response.data : []);
        setTotalPages(response.pagination?.totalPages || 1);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to fetch feedback.');
        setItems([]);
        setTotalPages(1);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadFeedback();
    return () => {
      cancelled = true;
    };
  }, [page, ratingFilter, debouncedSearch]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [page]);

  function goToPage(nextPage) {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page || loading) return;
    setPage(nextPage);
  }

  const pageItems = getPageItems(page, totalPages);

  return (
    <div className="payments-page main-bg-top">
      <div className="users-header feedback-header">
        <div className="users-filters">
          <div className="users-search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="filter-select-wrap">
            <select
              className="filter-select"
              value={ratingFilter}
              onChange={(e) => {
                setRatingFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
          </div>
        </div>
      </div>

      <div className="payments-table-scroll" ref={scrollRef}>
        <div className="users-table-wrap">
          <table className="users-table payments-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Phone</th>
                <th>Rating</th>
                <th>Feedback</th>
                <th>Date & Time</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="users-empty">Loading feedback...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="5" className="users-empty">{error}</td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan="5" className="users-empty">No feedback found.</td>
                </tr>
              ) : (
                items.map((item) => {
                  const name = item.user?.name || '—';
                  const email = item.user?.email || '';
                  const phone = item.user?.phone || '—';

                  return (
                    <tr key={item.id}>
                      <td>
                        <div className="user-cell">
                          <div className="user-cell-avatar">
                            {item.user?.image?.url ? (
                              <img
                                src={item.user.image.url}
                                alt={name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentElement.textContent = name !== '—' ? name.charAt(0).toUpperCase() : '?';
                                }}
                              />
                            ) : (
                              <span className="avatar-placeholder">
                                {name !== '—' ? name.charAt(0).toUpperCase() : '?'}
                              </span>
                            )}
                          </div>
                          <div className="user-cell-info">
                            <span className="user-cell-name">{name}</span>
                            <span className="user-cell-email">{email}</span>
                          </div>
                        </div>
                      </td>
                      <td>{phone}</td>
                      <td>
                        <StarRating rating={item.rating} />
                      </td>
                      <td className="feedback-text-cell">{item.text || '—'}</td>
                      <td className="feedback-datetime-cell">
                        <div className="datetime-cell">
                          <span className="datetime-date">{formatDate(item.createdAt)}</span>
                          <span className="datetime-time">{formatTime(item.createdAt)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && !error && totalPages > 1 && (
        <div className="payments-pagination" aria-label="Pagination">
          <button
            type="button"
            className="pagination-page-btn"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
            aria-label="Previous page"
          >
            ‹
          </button>

          {pageItems.map((item, index) =>
            item === '…' ? (
              <span key={`ellipsis-${index}`} className="pagination-ellipsis">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`pagination-page-btn ${page === item ? 'active' : ''}`}
                onClick={() => goToPage(item)}
                aria-current={page === item ? 'page' : undefined}
              >
                {item}
              </button>
            )
          )}

          <button
            type="button"
            className="pagination-page-btn"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
